// What a lesson's `startTime` means, and how to read it from another zone.
//
// ── The rule, stated once ───────────────────────────────────────────────────
//
// An eventSeries stores a pair:
//
//     startTime: '16:00'
//     timeZone:  'America/New_York'
//
// `startTime` is **wall-clock time in the series' own zone**. Four o'clock is
// four o'clock on the teacher's wall, all year, and neither the expansion
// (domain/recurrence.js), the calendar grid, nor daylight saving moves it —
// because nothing about the rule moves. A daylight-saving transition changes
// what instant that four o'clock is; it does not change the rule.
//
// A lesson is not anchored to the *student's* zone (#47). Two zones cross their
// daylight-saving boundaries on different dates, so a lesson anchored to the
// student would drift in the teacher's own calendar twice a year. A student's
// zone is used for **display only**, on the surfaces addressed to the family.
//
// ── Why the zone is stored on the series and not read from settings ─────────
//
// `settings/studio.timeZone` is the **default for new series** and what the
// reminder worker uses to decide what "today" means (domain/reminders.js). It
// is *not* the interpreter of existing lessons: with an ambient zone read at
// render time, a teacher who moves and changes one setting would reinterpret
// every lesson the studio has had, with nothing in the data recording it. The
// stored pair is self-describing: a move affects nothing until somebody edits
// the lessons.
//
// A series with no zone of its own is read in the studio's current zone, the
// only reading available for it; `seriesTimeZone` is the one place that is
// spelled out.
//
// ── A formatter, not a rule ────────────────────────────────────────────────
//
// No occurrence date, charge, balance, conflict or reminder is computed from a
// zone conversion — each is computed from `startDate`/`startTime` exactly as
// stored, on the UTC calendar grid. A conversion produces a *string a person
// reads*, on the surfaces addressed to somebody in another zone. So there are
// no golden vectors for it (see golden/generate.js): the Rust core has no
// time-zone database, and the conversion and abbreviation are the platform's.
//
// ── The date-only discipline ───────────────────────────────────────────────
//
// A date-only value constructed in local time lands a day early west of
// Greenwich. Nothing here constructs a `Date` from a date-only string in local
// time: every instant is built with `Date.UTC` and read back through
// `Intl.DateTimeFormat` with an explicit `timeZone`, and every date this module
// returns is a date-only string reassembled from Intl's own parts. The two
// exceptions, with no zone given, read an *instant* on the device's clock
// (`instantOfWallClockIn`, `wallClockIn`).

// ── The rules are the core's (#472, #474) ───────────────────────────────────
//
// Which stored value is a zone (`normaliseTimeZone`), which zone a series is
// written in (`seriesTimeZone`), and when a teacher's screen names it
// (`screenZoneLabel`'s decision) are `core/src/time_zone.rs`, so both phones
// answer alike. What is here is the conversion and the abbreviation, which
// need a time-zone database and are `Intl`'s, as they are `java.time`'s and
// Foundation's on the phones.

import { coreSync } from './core.js';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const HHMM = /^(\d{1,2}):(\d{2})$/;
const MS_DAY = 86400000;

// The bound `firestore.rules` puts on `settings/studio.timeZone`, repeated here
// so the domain refuses what the database would refuse. The longest IANA
// identifier in the tzdb is 32 characters; 80 is the rules file's slack.
export const MAX_TIME_ZONE_LENGTH = 80;

// A stored zone, or null. Bounded and shape-checked, never invented:
// `Area/Location`, or a single word — `UTC`, and the tz database's own `Japan`,
// `Singapore`, `Poland`, `ROK`. A shape check and not a membership test — the
// database gains and renames zones — so what this catches is prose. Whether the
// *runtime* knows the zone is `knowsTimeZone`'s question.
// The rule is `normalise_time_zone` in core/src/time_zone.rs; a value that is
// not a string is no zone.
export function normaliseTimeZone(value) {
  return coreSync().normaliseTimeZone(typeof value === 'string' ? value : null) ?? null;
}

// Whether this runtime's ICU data actually has the zone. Separate from
// `normaliseTimeZone`: a zone this container has never heard of is
// still the right thing to keep in the document — the next runtime may know it
// — but it is not something to hand to a formatter, which would throw.
export function knowsTimeZone(timeZone) {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

// The zone a series' `startTime` is written in.
//
// The series' own, or — for a series with none — the studio's current zone.
// Null when neither is known: a studio is given its device's zone at sign-up
// and on the first load that finds none (#620, `zoneToRecord` below), but a
// studio whose devices could not name a zone has none, and this does not
// invent one.
export function seriesTimeZone(series, studioTimeZone = null) {
  const own = series?.timeZone;
  return coreSync().seriesTimeZone(
    typeof own === 'string' ? own : null,
    typeof studioTimeZone === 'string' ? studioTimeZone : null,
  ) ?? null;
}

// The zone to store as the studio's own, or null (#620): `device` — the zone
// this machine is in — when `stored`, `settings/studio.timeZone` as the server
// holds it, is absent, and nothing otherwise. A stored zone is somebody's
// decision and is never replaced from here, even one `normaliseTimeZone`
// would refuse. Why the device's zone is recorded without asking is
// `recordStudioTimeZone`'s header in src/data/useSettings.jsx; the rule is
// `zone_to_record` in core/src/time_zone.rs, so both phones answer alike.
export function zoneToRecord(stored, device) {
  return coreSync().zoneToRecord(
    typeof stored === 'string' ? stored : null,
    typeof device === 'string' ? device : null,
  ) ?? null;
}

// ─── The conversion itself ──────────────────────────────────────────────────

// The wall clock in `timeZone` at a given instant, as strings.
function partsInZone(instantMs, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    // Without this, `hour: '2-digit'` with a 12-hour locale renders "04" for
    // both four in the morning and four in the afternoon, and an h24 cycle
    // renders midnight as "24". h23 is the only cycle whose hours are the
    // numbers 00–23.
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(instantMs);
  const out = {};
  for (const p of parts) if (p.type !== 'literal') out[p.type] = p.value;
  return out;
}

// How far ahead of UTC `timeZone` is at that instant, in milliseconds.
//
// Derived rather than looked up, because there is no API that returns it: the
// zone's own wall clock is formatted, then re-read as if it were UTC, and the
// difference between that and the real instant is the offset.
function offsetMsAt(instantMs, timeZone) {
  const p = partsInZone(instantMs, timeZone);
  const asIfUtc = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour) % 24, Number(p.minute), Number(p.second),
  );
  return asIfUtc - instantMs;
}

// A wall clock in some zone -> the instant it names.
//
// Two passes. The first guess uses the offset in force at the *naive* instant,
// which is the wrong side of a
// daylight-saving transition for any wall clock within an hour of one. The
// second pass asks again at the guessed instant, which is on the right side.
//
// The two genuinely ambiguous wall clocks — the hour that repeats when clocks
// go back, and the hour that does not exist when they go forward — resolve the
// way every implementation of this trick resolves them, and neither can arise
// from a lesson time a teacher would notice: an hour that does not exist has no
// instant to be shown at, and an hour that happens twice is shown at the first.
function instantOfWallClock(year, month, day, hour, minute, timeZone) {
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const guess = naive - offsetMsAt(naive, timeZone);
  return naive - offsetMsAt(guess, timeZone);
}

function gridOf(dateISO) {
  const m = DATE_ONLY.exec(dateISO);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

// A wall clock in one zone, read as a wall clock in another.
//
// Takes a date-only string and an `HH:MM`, returns a date-only string and an
// `HH:MM` — the same currency in and out, so no caller is ever handed an
// instant it might construct a local `Date` from. `dayOffset` is what a family
// most needs to be told: a four o'clock lesson in New York is five in the
// morning *the next day* in Seoul, and a converted time printed without its
// date is a worse lie than the unconverted one.
//
// Null when anything is missing, malformed, or a zone this runtime cannot
// resolve — never a guess. Callers fall back to printing the stored time.
export function convertWallClock(dateISO, hhmm, fromZone, toZone) {
  const d = DATE_ONLY.exec(String(dateISO || ''));
  const t = HHMM.exec(String(hhmm || '').trim());
  const from = normaliseTimeZone(fromZone);
  const to = normaliseTimeZone(toZone);
  if (!d || !t || !from || !to) return null;
  const hour = Number(t[1]);
  const minute = Number(t[2]);
  if (hour > 23 || minute > 59) return null;
  try {
    const instant = instantOfWallClock(
      Number(d[1]), Number(d[2]), Number(d[3]), hour, minute, from,
    );
    const p = partsInZone(instant, to);
    const date = `${p.year}-${p.month}-${p.day}`;
    const time = `${String(Number(p.hour) % 24).padStart(2, '0')}:${p.minute}`;
    const grid = gridOf(date);
    const base = gridOf(String(dateISO));
    return {
      date,
      time,
      dayOffset: grid == null || base == null ? 0 : Math.round((grid - base) / MS_DAY),
    };
  } catch {
    return null;
  }
}

// A wall clock in a zone, as the instant it names in epoch milliseconds — a
// date and time a teacher picked, read on the clock they were shown (#599,
// New Message's "Automatically at a future date/time"). Null when either is
// malformed or the zone is one this runtime cannot resolve; with no zone at
// all, the device's own clock.
export function instantOfWallClockIn(dateISO, hhmm, timeZone = null) {
  const d = DATE_ONLY.exec(String(dateISO || ''));
  const t = HHMM.exec(String(hhmm || '').trim());
  if (!d || !t) return null;
  const [year, month, day, hour, minute] = [d[1], d[2], d[3], t[1], t[2]].map(Number);
  if (hour > 23 || minute > 59) return null;
  const zone = normaliseTimeZone(timeZone);
  if (!zone) {
    // Local time on purpose, and only here: this is an instant, not a
    // date-only value, and with no zone the clock the teacher was shown is
    // the device's.
    const local = new Date(year, month - 1, day, hour, minute).getTime();
    return Number.isFinite(local) ? local : null;
  }
  try {
    return instantOfWallClock(year, month, day, hour, minute, zone);
  } catch {
    return null;
  }
}

// The wall clock in a zone at an instant: `{ date, time }`, a date-only string
// and an `HH:MM`, or null for a zone this runtime cannot resolve. With no zone,
// the device's.
export function wallClockIn(instantMs, timeZone = null) {
  const zone = normaliseTimeZone(timeZone);
  try {
    if (!zone) {
      const at = new Date(instantMs);
      const two = (n) => String(n).padStart(2, '0');
      return {
        date: `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())}`,
        time: `${two(at.getHours())}:${two(at.getMinutes())}`,
      };
    }
    const p = partsInZone(instantMs, zone);
    return { date: `${p.year}-${p.month}-${p.day}`, time: `${String(Number(p.hour) % 24).padStart(2, '0')}:${p.minute}` };
  } catch {
    return null;
  }
}

// MMS's default for "Automatically at a future date/time": today, at the next
// whole hour on the zone's clock.
export function nextWholeHourIn(nowMs, timeZone = null) {
  const now = wallClockIn(nowMs, timeZone);
  if (!now) return null;
  const top = instantOfWallClockIn(now.date, `${now.time.slice(0, 2)}:00`, timeZone);
  return top == null ? null : wallClockIn(top + 3600000, timeZone);
}

// "EDT", "GMT+9", "UTC" — what a reader needs to see next to a bare clock
// reading before it means anything.
//
// Asked of the zone *at that lesson's own instant*, not at the present moment:
// a lesson in January is EST and one in July is EDT, and labelling both with
// today's abbreviation would be wrong for half the year.
//
// An abbreviation is a CLDR string that differs by locale ("GMT+9" against
// "UTC+9"), so every caller passes the studio's: the calendar
// (screenZoneLabel) and the emails and texts a studio sends
// (functions/delivery/templates.js, #500), which write the lesson the way the
// studio's screens do. The en-US default is for a test.
export function zoneAbbreviation(timeZone, dateISO, hhmm, locale = 'en-US') {
  const zone = normaliseTimeZone(timeZone);
  const d = DATE_ONLY.exec(String(dateISO || ''));
  const t = HHMM.exec(String(hhmm || '').trim());
  if (!zone || !d || !t) return '';
  try {
    const instant = instantOfWallClock(
      Number(d[1]), Number(d[2]), Number(d[3]), Number(t[1]), Number(t[2]), zone,
    );
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: zone, hour: 'numeric', timeZoneName: 'short',
    }).formatToParts(instant);
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

// ─── The policy every family-facing surface shares ─────────────────────────

// Whether a *teacher's own screen* should name the zone next to a lesson time,
// and what to name it.
//
// Almost always nothing: a studio's calendar is drawn in wall-clock time, and
// a lesson created in the zone the teacher is in needs no label. The case it
// serves is the teacher who moved: old lessons keep the zone they were created
// in (#47), so they sit at the same grid positions while no longer being at
// those times where the teacher now is.
//
// Empty when there is nothing to say: no zone on either side, the two
// agreeing, or two identifiers that produce the same clock reading anyway.
//
// The decision is the core's (`screen_label_zones`, `screen_zone_label`), and
// both phones ask it with their own platform's conversion; the conversion and
// the abbreviation here are `Intl`'s.
export function screenZoneLabel(seriesZone, studioZone, dateISO, hhmm, locale = 'en-US') {
  const zones = coreSync().screenLabelZones(
    typeof seriesZone === 'string' ? seriesZone : null,
    typeof studioZone === 'string' ? studioZone : null,
  );
  if (!zones) return '';
  const shifted = convertWallClock(dateISO, hhmm, zones.from, zones.to);
  return coreSync().screenZoneLabel(
    String(hhmm ?? ''),
    shifted ? shifted.time : null,
    shifted ? shifted.dayOffset : 0,
    shifted ? zoneAbbreviation(zones.from, dateISO, hhmm, locale) : '',
  );
}

// What to print for one lesson's time, once — here rather than at each call
// site in functions/delivery/templates.js, so "when does a second reading
// appear" has one answer. The shape it returns:
//
//   { zone, date, time, label, viewer }
//
// `zone`/`date`/`time` are the lesson exactly as stored — the studio's own
// reading, never converted — and `label` is its abbreviation. `viewer` is the
// recipient's reading, and it is null unless there is something else to say:
//
//   * no zone on the series and none on the studio — nothing to convert from,
//     and nothing to label with either;
//   * no zone known for the recipient — the common case, and the studio's own
//     labelled time is the right thing to print;
//   * the two zones produce the same clock reading on the same day. America/
//     Toronto and America/New_York are different identifiers for the same
//     wall clock.
export function timeReadings({
  date, time, seriesZone, viewerZone = null, locale = 'en-US',
}) {
  const zone = normaliseTimeZone(seriesZone);
  const base = {
    zone,
    date: DATE_ONLY.test(String(date || '')) ? String(date) : null,
    time: HHMM.test(String(time || '').trim()) ? String(time).trim() : null,
    label: zone ? zoneAbbreviation(zone, date, time, locale) : '',
    viewer: null,
  };
  const viewer = normaliseTimeZone(viewerZone);
  if (!zone || !viewer || viewer === zone) return base;

  const shifted = convertWallClock(date, time, zone, viewer);
  if (!shifted) return base;
  if (shifted.time === base.time && shifted.dayOffset === 0) return base;

  return {
    ...base,
    viewer: {
      zone: viewer,
      date: shifted.date,
      time: shifted.time,
      dayOffset: shifted.dayOffset,
      label: zoneAbbreviation(viewer, shifted.date, shifted.time, locale),
    },
  };
}
